import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, type User } from '../store/authStore';
import client from '../api/client';
import { toDateInputValue } from '../utils/dateInput';
import { todayIsoDate, validatePolicyStartDate } from '../components/PolicyStartDateField';
import { trackCarFunnelStep, trackGenerateLead } from '../utils/analytics';
import {
  clearLeadSession,
  isLeadEnrichmentStarted,
  readLeadIdFromSession,
  wasLeadConversionFired,
} from '../utils/leadSession';
import { isTexasZipCode } from '../utils/texasZipCode';
import '../components/CarInsuranceAdLandingContent.css';
import './CarInsuranceFormPage.css';

const currentCalendarYear = new Date().getFullYear();
const DECLARATIONS_EMAIL = 'GetSkimp@GetSkimp.com';

type CarInsuranceFormPageProps = {
  /** Ad landing page — skip /qualify and validate Texas zip at submit. */
  directLanding?: boolean;
};

/** Letters and digits only; max length 17 (normalized to uppercase). */
function sanitizeVinInput(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17);
}

const VIN_COMPLETE_PATTERN = /^[A-Z0-9]{17}$/;

function maskTxDl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.length <= 4) return trimmed;
  return `••••${trimmed.slice(-4)}`;
}

type CoverageType = 'liability_only' | 'full_coverage';

interface VehicleFormRow {
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleVin: string;
  vinPendingLater: boolean;
  coverageType: CoverageType;
}

interface AdditionalDriverFormRow {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  txDl: string;
  email: string;
}

const emptyVehicle = (): VehicleFormRow => ({
  vehicleYear: '',
  vehicleMake: '',
  vehicleModel: '',
  vehicleVin: '',
  vinPendingLater: false,
  coverageType: 'full_coverage',
});

export type CarInsuranceApplicationPayload = {
  applicantFullName?: string;
  applicantDateOfBirth: string;
  applicantTxDl: string;
  homeStreetAddress: string;
  homeAddressLine2: string;
  homeCity: string;
  homeState: string;
  homeZip: string;
  additionalDrivers: Array<{
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    txDl: string;
    email?: string;
  }>;
  hasActiveAutoPolicy: boolean;
  vehicles: Array<{
    vehicleYear: number;
    vehicleMake: string;
    vehicleModel: string;
    vehicleVin: string;
    vinPendingLater?: boolean;
    coverageType: CoverageType;
  }>;
  declarationPendingLater?: boolean;
  policyStartDate: 'decide_later' | string;
  /** Guest lead id from the short contact form, when present. */
  leadId?: number;
};

function buildPayload(
  applicantDateOfBirth: string,
  applicantTxDl: string,
  homeStreetAddress: string,
  homeAddressLine2: string,
  homeCity: string,
  homeState: string,
  homeZip: string,
  additionalDrivers: AdditionalDriverFormRow[],
  intakeMethod: 'email_declarations' | 'manual_entry',
  vehicles: VehicleFormRow[],
  policyStartDate: 'decide_later' | string
): CarInsuranceApplicationPayload {
  const hasActiveAutoPolicy = intakeMethod === 'email_declarations';
  const vehiclesPayload = hasActiveAutoPolicy
    ? []
    : vehicles.map((v) => ({
        vehicleYear: parseInt(v.vehicleYear, 10),
        vehicleMake: v.vehicleMake.trim(),
        vehicleModel: v.vehicleModel.trim(),
        vehicleVin: v.vehicleVin.trim(),
        vinPendingLater: v.vinPendingLater === true,
        coverageType: v.coverageType,
      }));

  const leadId = readLeadIdFromSession();

  return {
    applicantFullName: '',
    applicantDateOfBirth,
    applicantTxDl: applicantTxDl.trim(),
    homeStreetAddress: homeStreetAddress.trim(),
    homeAddressLine2: homeAddressLine2.trim(),
    homeCity: homeCity.trim(),
    homeState: homeState.trim(),
    homeZip,
    additionalDrivers: additionalDrivers
      .filter((d) => d.firstName.trim() || d.lastName.trim() || d.dateOfBirth.trim() || d.txDl.trim() || d.email.trim())
      .map((d) => ({
        firstName: d.firstName.trim(),
        lastName: d.lastName.trim(),
        dateOfBirth: d.dateOfBirth.trim(),
        txDl: d.txDl.trim(),
        email: d.email.trim(),
      })),
    hasActiveAutoPolicy,
    vehicles: vehiclesPayload,
    declarationPendingLater: hasActiveAutoPolicy,
    policyStartDate,
    ...(leadId != null ? { leadId } : {}),
  };
}

function CarInsuranceFormPage({ directLanding = false }: CarInsuranceFormPageProps) {
  const navigate = useNavigate();
  const { isAuthenticated, setUser } = useAuthStore();

  const refreshUserFromProfile = async () => {
    try {
      const { data } = await client.get<{ user: User }>('/users/profile');
      const cur = useAuthStore.getState().user;
      if (cur) setUser({ ...cur, ...data.user });
    } catch {
      /* ignore */
    }
  };

  const [applicantDateOfBirth, setApplicantDateOfBirth] = useState('');
  const [txDlExpanded, setTxDlExpanded] = useState(false);
  const [applicantTxDl, setApplicantTxDl] = useState('');
  const [homeStreetAddress, setHomeStreetAddress] = useState('');
  const [homeAddressLine2, setHomeAddressLine2] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [homeState, setHomeState] = useState('Texas');
  const [homeZip, setHomeZip] = useState('');
  const [drivers, setDrivers] = useState<AdditionalDriverFormRow[]>([]);
  const [policyStartMode, setPolicyStartMode] = useState<'no_preference' | 'choose_date'>('no_preference');
  const [policyStartDate, setPolicyStartDate] = useState(todayIsoDate);
  const [intakeMethod, setIntakeMethod] = useState<'email_declarations' | 'manual_entry'>('email_declarations');
  const [vehicles, setVehicles] = useState<VehicleFormRow[]>([emptyVehicle()]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeclarationsHelp, setShowDeclarationsHelp] = useState(false);
  const [showVinHelp, setShowVinHelp] = useState(false);

  useEffect(() => {
    const fromEnrichment = isLeadEnrichmentStarted();
    trackCarFunnelStep('form_view', {
      entry_point: fromEnrichment ? 'enrichment' : directLanding ? 'ad_landing' : 'qualify',
    });
  }, [directLanding]);

  useEffect(() => {
    const zip = sessionStorage.getItem('zipCode') || '';

    if (directLanding) {
      sessionStorage.setItem('insuranceType', 'car');
      if (zip.length === 5) setHomeZip(zip);
      return;
    }

    const type = sessionStorage.getItem('insuranceType');
    if (type !== 'car' || zip.length !== 5) {
      navigate('/qualify');
      return;
    }
    setHomeZip(zip);
  }, [directLanding, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const sessionZip = sessionStorage.getItem('zipCode') || '';
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get<{ user: User }>('/users/profile');
        if (cancelled) return;
        const p = data.user;
        setHomeStreetAddress((a) => (a.trim() ? a : p.address || ''));
        setHomeCity((c) => p.city || c || '');
        setHomeState((s) => p.state || s || 'Texas');
        setHomeZip((z) => (z.trim() ? z : p.zipCode || sessionZip || ''));
        setApplicantDateOfBirth((d) => (d.trim() ? d : toDateInputValue(p.dateOfBirth)));
        const cur = useAuthStore.getState().user;
        if (cur) setUser({ ...cur, ...data.user });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, setUser]);

  const updateVehicle = (index: number, patch: Partial<VehicleFormRow>) => {
    setVehicles((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addVehicle = () => setVehicles((prev) => [...prev, emptyVehicle()]);
  const removeVehicle = (index: number) => {
    setVehicles((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const addDriver = () =>
    setDrivers((prev) => [...prev, { firstName: '', lastName: '', dateOfBirth: '', txDl: '', email: '' }]);
  const removeDriver = (index: number) =>
    setDrivers((prev) => prev.filter((_, i) => i !== index));
  const updateDriver = (index: number, patch: Partial<AdditionalDriverFormRow>) =>
    setDrivers((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const validateClient = (): string | null => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(applicantDateOfBirth)) return 'Date of birth must be YYYY-MM-DD.';
    if (!homeStreetAddress.trim() || !homeCity.trim() || !homeState.trim()) return 'Complete your home address.';
    if (!/^\d{5}$/.test(homeZip)) return 'Home zip code must be 5 digits.';
    if (directLanding && !isTexasZipCode(homeZip)) {
      return 'We currently offer car insurance in Texas only.';
    }
    if (policyStartMode === 'choose_date') {
      const policyStartErr = validatePolicyStartDate(policyStartDate);
      if (policyStartErr) return policyStartErr;
    }
    for (let i = 0; i < drivers.length; i++) {
      const d = drivers[i];
      if (!d.firstName.trim() || !d.lastName.trim()) return `Enter first and last name for additional driver ${i + 1}.`;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d.dateOfBirth)) return `Driver ${i + 1} DOB must be YYYY-MM-DD.`;
    }
    if (intakeMethod === 'manual_entry') {
      if (vehicles.length < 1) return 'Add at least one vehicle.';
      for (let i = 0; i < vehicles.length; i++) {
        const v = vehicles[i];
        const y = parseInt(v.vehicleYear, 10);
        if (!v.vehicleMake.trim() || !v.vehicleModel.trim()) {
          return `Complete all fields for vehicle ${i + 1}.`;
        }
        if (!v.vinPendingLater && !v.vehicleVin.trim()) return `Enter VIN or choose Add VIN later for vehicle ${i + 1}.`;
        if (!v.vinPendingLater && !VIN_COMPLETE_PATTERN.test(v.vehicleVin.trim()))
          return `Vehicle ${i + 1}: VIN must be exactly 17 letters or numbers.`;
        if (Number.isNaN(y) || y < 1900 || y > currentCalendarYear + 1) {
          return `Enter a valid year for vehicle ${i + 1}.`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const msg = validateClient();
    if (msg) {
      setError(msg);
      return;
    }

    const resolvedPolicyStart = policyStartMode === 'no_preference' ? 'decide_later' : policyStartDate;
    const resolvedTxDl = applicantTxDl.trim();

    const payload = buildPayload(
      applicantDateOfBirth,
      resolvedTxDl,
      homeStreetAddress,
      homeAddressLine2,
      homeCity,
      homeState,
      homeZip,
      drivers,
      intakeMethod,
      vehicles,
      resolvedPolicyStart
    );

    trackCarFunnelStep('form_submit', {
      logged_in: isAuthenticated ? 'yes' : 'no',
      entry_point: isLeadEnrichmentStarted()
        ? 'enrichment'
        : directLanding
          ? 'ad_landing'
          : 'qualify',
    });

    if (!isAuthenticated) {
      trackCarFunnelStep('signup_redirect');
      sessionStorage.setItem('carInsuranceFormData', JSON.stringify(payload));
      sessionStorage.setItem('insuranceType', 'car');
      sessionStorage.setItem('zipCode', homeZip);
      sessionStorage.setItem('redirectAfterSignup', '/insurance/requests');
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      await client.post('/insurance/car-request', payload);
      if (!wasLeadConversionFired()) {
        await trackGenerateLead('car');
      }
      await refreshUserFromProfile();
      sessionStorage.removeItem('carInsuranceFormData');
      sessionStorage.removeItem('insuranceType');
      sessionStorage.removeItem('zipCode');
      clearLeadSession();
      navigate('/insurance/requests');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; message?: string } } };
      setError(ax.response?.data?.error || ax.response?.data?.message || 'Failed to submit your request');
    } finally {
      setLoading(false);
    }
  };

  const formCard = (
    <div className="card car-form-card">
      {!directLanding && (
        <>
          <div className="card-header">Car insurance quote</div>
          <p style={{ color: '#4B5563', marginBottom: '1.25rem' }}>
            Share a few details about your vehicle and we will follow up with you to find the right coverage.
          </p>
        </>
      )}

      {directLanding && (
        <div className="car-ad-form-intro">
          <h2 className="car-ad-form-intro-title">Your quote</h2>
          <p className="car-ad-form-intro-text">Takes about 3 minutes. We&apos;ll follow up with options.</p>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
          <h4 className="car-insurance-section-heading">Date of birth &amp; Texas DL</h4>
          <div className="car-insurance-dob-dl-grid">
            <div className="form-group">
              <label htmlFor="applicantDateOfBirth">Date of birth *</label>
              <input
                id="applicantDateOfBirth"
                type="date"
                value={applicantDateOfBirth}
                onChange={(e) => setApplicantDateOfBirth(e.target.value)}
                required
              />
            </div>
            <div className="form-group car-tx-dl-accordion-wrap">
              <span id="car-tx-dl-label" className="car-label-text">
                Texas driver license #{' '}
                <span className="car-optional-label">(optional)</span>
              </span>
              <div className="car-tx-dl-accordion">
                <button
                  type="button"
                  className="car-tx-dl-accordion-trigger"
                  aria-expanded={txDlExpanded}
                  aria-controls="car-tx-dl-panel"
                  id="car-tx-dl-trigger"
                  onClick={() => setTxDlExpanded((open) => !open)}
                >
                  {applicantTxDl.trim() && !txDlExpanded ? (
                    <>
                      <span className="car-tx-dl-accordion-summary">{maskTxDl(applicantTxDl)}</span>
                      <span className="car-tx-dl-accordion-action">Edit</span>
                    </>
                  ) : (
                    <>
                      <span>{txDlExpanded ? 'Add license # now' : 'Add later'}</span>
                      <span
                        className={`car-tx-dl-chevron${txDlExpanded ? ' car-tx-dl-chevron--open' : ''}`}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </button>
                {!txDlExpanded && !applicantTxDl.trim() && (
                  <p className="car-tx-dl-accordion-hint">
                    You can share this with us later.
                  </p>
                )}
                {txDlExpanded && (
                  <div
                    id="car-tx-dl-panel"
                    role="region"
                    aria-labelledby="car-tx-dl-trigger"
                    className="car-tx-dl-accordion-panel"
                  >
                    <input
                      id="applicantTxDl"
                      value={applicantTxDl}
                      onChange={(e) => setApplicantTxDl(e.target.value)}
                      autoComplete="off"
                      placeholder="Texas driver license #"
                      aria-labelledby="car-tx-dl-label"
                    />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm car-tx-dl-clear-btn"
                      onClick={() => {
                        setApplicantTxDl('');
                        setTxDlExpanded(false);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <h4 className="car-insurance-section-heading">Home address</h4>
          <div className="form-group">
            <label htmlFor="homeStreetAddress">Street address *</label>
            <input
              id="homeStreetAddress"
              value={homeStreetAddress}
              onChange={(e) => setHomeStreetAddress(e.target.value)}
              required
              autoComplete="street-address"
            />
          </div>
          <div className="form-group">
            <label htmlFor="homeAddressLine2">Apt / suite (optional)</label>
            <input
              id="homeAddressLine2"
              value={homeAddressLine2}
              onChange={(e) => setHomeAddressLine2(e.target.value)}
              autoComplete="address-line2"
            />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '1rem',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div className="form-group" style={{ minWidth: 0 }}>
              <label htmlFor="homeCity">City *</label>
              <input
                id="homeCity"
                value={homeCity}
                onChange={(e) => setHomeCity(e.target.value)}
                required
                style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ minWidth: 0 }}>
              <label htmlFor="homeState">State *</label>
              <input
                id="homeState"
                value={homeState}
                onChange={(e) => setHomeState(e.target.value)}
                required
                style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ minWidth: 0 }}>
              <label htmlFor="homeZip">Zip *</label>
              <input
                id="homeZip"
                value={homeZip}
                onChange={(e) => setHomeZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                maxLength={5}
                required
                autoComplete="postal-code"
                placeholder={directLanding ? 'Texas zip' : '5-digit zip'}
                style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <section className="car-policy-start-section" aria-labelledby="car-policy-start-heading">
            <h4 id="car-policy-start-heading" className="car-insurance-section-heading" style={{ marginTop: '1.5rem' }}>
              Policy start date{' '}
              <span className="car-optional-label" style={{ fontSize: '0.95rem', fontWeight: 500 }}>
                (optional)
              </span>
            </h4>
            <div className="car-choice-row" role="radiogroup" aria-labelledby="car-policy-start-heading">
              <label className="car-choice-option">
                <input
                  type="radio"
                  name="policyStartMode"
                  checked={policyStartMode === 'no_preference'}
                  onChange={() => setPolicyStartMode('no_preference')}
                />
                Skip for now
              </label>
              <label className="car-choice-option">
                <input
                  type="radio"
                  name="policyStartMode"
                  checked={policyStartMode === 'choose_date'}
                  onChange={() => setPolicyStartMode('choose_date')}
                />
                Choose a date
              </label>
            </div>
            {policyStartMode === 'choose_date' && (
              <div className="form-group car-policy-start-date">
                <label htmlFor="carPolicyStartDate" className="car-sr-only">
                  Policy start date
                </label>
                <input
                  id="carPolicyStartDate"
                  type="date"
                  min={todayIsoDate()}
                  value={policyStartDate}
                  onChange={(e) => setPolicyStartDate(e.target.value)}
                  style={{ maxWidth: '16rem' }}
                />
              </div>
            )}
          </section>

          <h4 className="car-insurance-section-heading">Spouse &amp; additional drivers</h4>
          <button type="button" className="btn btn-outline btn-sm" onClick={addDriver}>
            + Add additional driver
          </button>
          {drivers.length > 0 &&
            drivers.map((d, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid var(--color-grey, #e5e7eb)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '1rem',
                  background: 'var(--color-light-grey, #f9fafb)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>Additional driver {index + 1}</strong>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => removeDriver(index)}>
                    Remove
                  </button>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr)',
                    gap: '1rem',
                    marginTop: '0.75rem',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
                    <label>First name *</label>
                    <input value={d.firstName} onChange={(e) => updateDriver(index, { firstName: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
                    <label>Last name *</label>
                    <input value={d.lastName} onChange={(e) => updateDriver(index, { lastName: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
                    <label>Date of birth *</label>
                    <input
                      type="date"
                      value={d.dateOfBirth}
                      onChange={(e) => updateDriver(index, { dateOfBirth: e.target.value })}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
                    <label>Texas DL # (optional)</label>
                    <input value={d.txDl} onChange={(e) => updateDriver(index, { txDl: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
                    <label>Email (optional)</label>
                    <input
                      type="email"
                      value={d.email}
                      onChange={(e) => updateDriver(index, { email: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}

          <h4 className="car-insurance-section-heading">Auto policy &amp; vehicles</h4>
          <p className="car-section-intro">
            Share your vehicle details by email or enter them below.
          </p>
          <div style={{ marginBottom: showDeclarationsHelp ? '0.5rem' : '1rem' }}>
            <button
              type="button"
              id="declarations-help-trigger"
              aria-expanded={showDeclarationsHelp}
              aria-controls="declarations-help-panel"
              onClick={() => setShowDeclarationsHelp((open) => !open)}
              className="car-insurance-help-toggle"
            >
              What&apos;s a declarations page?
            </button>
          </div>
          {showDeclarationsHelp && (
            <div
              id="declarations-help-panel"
              role="region"
              aria-labelledby="declarations-help-trigger"
              style={{
                marginBottom: '1rem',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                background: 'var(--color-light-grey, #f3f4f6)',
                border: '1px solid var(--color-grey, #e5e7eb)',
              }}
            >
              <p style={{ margin: 0, color: '#4B5563', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 }}>
                It&apos;s the short summary your current auto insurer gives you with your policy. It lists your vehicles, coverages, and policy dates so we can match your coverage.
              </p>
            </div>
          )}

          <div className="form-group">
            <span className="car-label-text">How would you like to continue? *</span>
            <div className="car-intake-row car-intake-row--stacked" role="group" aria-label="How would you like to continue?">
              <button
                type="button"
                className={`car-intake-choice ${intakeMethod === 'email_declarations' ? 'car-intake-choice--selected' : ''}`}
                onClick={() => {
                  setIntakeMethod('email_declarations');
                  setShowVinHelp(false);
                }}
              >
                I&apos;ll email my declarations page to {DECLARATIONS_EMAIL} later
              </button>
              <button
                type="button"
                className={`car-intake-choice ${intakeMethod === 'manual_entry' ? 'car-intake-choice--selected' : ''}`}
                onClick={() => {
                  setIntakeMethod('manual_entry');
                  setShowDeclarationsHelp(false);
                }}
              >
                Enter vehicles manually
              </button>
            </div>
          </div>

          {intakeMethod === 'manual_entry' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <button
                  type="button"
                  className="car-insurance-help-toggle"
                  aria-expanded={showVinHelp}
                  onClick={() => setShowVinHelp((open) => !open)}
                >
                  What&apos;s a VIN?
                </button>
                {showVinHelp && (
                  <p className="car-insurance-help-text">
                    A VIN (Vehicle Identification Number) is a unique 17-character code for your vehicle. Insurers use it to identify the exact car or truck you are quoting. You can find it on your registration, insurance ID card, the driver&apos;s door jamb label, or on the dashboard visible through the windshield on the driver&apos;s side. Use only letters A through Z and digits 0 through 9 (no spaces).
                  </p>
                )}
              </div>
              {vehicles.map((v, index) => (
                <div
                  key={index}
                  style={{
                    border: '1px solid var(--color-grey, #e5e7eb)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    background: 'var(--color-light-grey, #f9fafb)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>Vehicle {index + 1}</strong>
                    {vehicles.length > 1 && (
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => removeVehicle(index)}>
                        Remove
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginTop: '0.75rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Year *</label>
                      <input
                        type="number"
                        min={1900}
                        max={currentCalendarYear + 1}
                        value={v.vehicleYear}
                        onChange={(e) => updateVehicle(index, { vehicleYear: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Make *</label>
                      <input value={v.vehicleMake} onChange={(e) => updateVehicle(index, { vehicleMake: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Model *</label>
                      <input value={v.vehicleModel} onChange={(e) => updateVehicle(index, { vehicleModel: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                      <label>VIN {v.vinPendingLater ? '(adding later)' : '*'}</label>
                      {!v.vinPendingLater ? (
                        <>
                          <input
                            value={v.vehicleVin}
                            maxLength={17}
                            inputMode="text"
                            autoCapitalize="characters"
                            spellCheck={false}
                            aria-describedby={`vehicle-${index}-vin-hint`}
                            onChange={(e) => updateVehicle(index, { vehicleVin: sanitizeVinInput(e.target.value) })}
                          />
                          <p id={`vehicle-${index}-vin-hint`} style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                            Exactly 17 characters, letters and numbers only.
                          </p>
                          <div style={{ marginTop: '0.5rem' }}>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={() => updateVehicle(index, { vinPendingLater: true, vehicleVin: '' })}
                            >
                              I&apos;ll add my VIN later
                            </button>
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            padding: '0.75rem',
                            border: '1px solid var(--color-grey, #e5e7eb)',
                            borderRadius: '8px',
                            background: 'var(--color-light-grey, #f3f4f6)',
                          }}
                        >
                          <p style={{ margin: '0 0 0.5rem', color: '#374151' }}>VIN will be provided later for this vehicle.</p>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => updateVehicle(index, { vinPendingLater: false })}
                          >
                            Enter VIN now
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                      <span className="car-label-text" style={{ fontSize: '0.9rem' }}>
                        Coverage for this vehicle *
                      </span>
                      <div className="car-coverage-row" role="group" aria-label={`Coverage for vehicle ${index + 1}`}>
                        <button
                          type="button"
                          className={`car-coverage-choice ${v.coverageType === 'full_coverage' ? 'car-coverage-choice--selected' : ''}`}
                          onClick={() => updateVehicle(index, { coverageType: 'full_coverage' })}
                        >
                          Full coverage
                        </button>
                        <button
                          type="button"
                          className={`car-coverage-choice ${v.coverageType === 'liability_only' ? 'car-coverage-choice--selected' : ''}`}
                          onClick={() => updateVehicle(index, { coverageType: 'liability_only' })}
                        >
                          Liability only
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-secondary" onClick={addVehicle}>
                + Add another vehicle
              </button>
            </>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ flex: 1 }}
              onClick={() => {
                if (isLeadEnrichmentStarted()) {
                  navigate('/get-quote/thank-you');
                  return;
                }
                navigate(directLanding ? '/car-insurance' : '/qualify');
              }}
            >
              Back
            </button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
              {loading ? 'Submitting...' : isAuthenticated ? 'Submit quote request' : 'Continue to create account'}
            </button>
          </div>
        </form>
    </div>
  );

  return (
    <div
      className={`container car-form-page${directLanding ? ' car-ad-form-page' : ''}`}
      style={{ maxWidth: '700px', margin: '2rem auto' }}
    >
      {formCard}
    </div>
  );
}

export default CarInsuranceFormPage;